import React from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { CircleHelp } from "lucide-react";

/**
 * A qualifying question raised from the agent's DIM1 guidance, rendered as a
 * pending task in the chat thread.
 *
 * There is deliberately no text field here: the thread's own composer takes the
 * typed answer, so exactly one answer composer is active per pending question.
 * The chips are a shortcut for the suggested options, not a second input.
 *
 * The qualifying step sees the message text, variables, earlier clarification
 * answers and (when enabled) recent memory — never attachment contents or OCR.
 * Files are processed after qualification, so a detail that lives only inside an
 * attachment still has to be typed. `hasAttachments` surfaces that boundary
 * instead of implying the document has already been read.
 *
 * `status`:
 *   pending    — waiting on the user
 *   answered   — the reply was submitted (the id is spent)
 *   cancelling — a cancel is in flight
 *   cancelled  — the exchange was abandoned
 *   expired    — the window lapsed before an answer
 */
const QualificationCard = ({
  question,
  status = "pending",
  answer,
  secondsLeft = 0,
  hasAttachments = false,
  isBusy = false,
  onAnswer,
  onCancel,
  isDarkMode,
}) => {
  if (!question) return null;

  const isPending = status === "pending";
  const isCancelling = status === "cancelling";
  const isExpired = status === "expired";
  const disabled = isBusy || isCancelling || !isPending;

  const expiryLabel = () => {
    if (secondsLeft <= 0) return "Expired";
    const minutes = Math.ceil(secondsLeft / 60);
    return minutes <= 1 ? "Expires in under a minute" : `Expires in ${minutes} min`;
  };

  const statusLine = () => {
    if (isExpired) {
      return "This question expired. Send your request again, including the missing details.";
    }
    if (status === "answered") {
      return answer ? `Answered: ${answer}` : "Answered.";
    }
    if (status === "cancelled") return "Cancelled.";
    if (isCancelling) return "Cancelling...";
    return null;
  };

  const line = statusLine();

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: isPending
          ? isDarkMode
            ? "rgba(227,98,80,0.55)"
            : "rgba(227,98,80,0.45)"
          : "divider",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 1.75 },
        backgroundColor: isPending
          ? isDarkMode
            ? "rgba(227,98,80,0.10)"
            : "rgba(227,98,80,0.06)"
          : "transparent",
      }}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <CircleHelp
          size={18}
          strokeWidth={2}
          aria-hidden
          style={{ flexShrink: 0, marginTop: 2, color: "#e36250" }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 700,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            Needs more details
          </Typography>
          {/* Plain text on purpose — the question comes from customer documents. */}
          {/* Up to ~240 chars and may open with a restated premise — wrap, never truncate. */}
          <Typography
            variant="body2"
            sx={{ mt: 0.5, fontWeight: 600, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {question.question || "The agent needs one more detail before it can answer."}
          </Typography>
        </Box>
      </Box>

      {isPending && hasAttachments && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, pl: 3.25, color: "text.secondary", lineHeight: 1.5 }}
        >
          Please enter this detail here, even if it is included in your attachment. Attachment
          contents are only processed after this step.
        </Typography>
      )}

      {isPending && question.options.length > 0 && (
        <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {question.options.map((option, index) => (
            <Chip
              key={`${question.qualificationId}-option-${index}`}
              size="small"
              variant="outlined"
              clickable={!disabled}
              disabled={disabled}
              label={option}
              onClick={() => onAnswer?.(option)}
              sx={{
                maxWidth: "100%",
                height: "auto",
                py: 0.5,
                "& .MuiChip-label": { whiteSpace: "normal", display: "block", lineHeight: 1.4 },
              }}
            />
          ))}
        </Box>
      )}

      {isPending && (
        <Box
          sx={{
            mt: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {question.options.length > 0
              ? "Pick one, or type your answer below."
              : "Type your answer below."}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {expiryLabel()}
          </Typography>
          <Button size="small" color="inherit" onClick={onCancel} disabled={disabled}>
            Cancel
          </Button>
        </Box>
      )}

      {line && (
        <Box sx={{ mt: 1.25, display: "flex", alignItems: "center", gap: 0.75 }}>
          {isCancelling && <CircularProgress size={12} />}
          <Typography variant="caption" sx={{ color: isExpired ? "warning.main" : "text.secondary" }}>
            {line}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QualificationCard;
